// AGC: MemberProfile - Complete detailed view for a team member
// AGC: Shows member info, tasks, equipment used, hours worked, and permissions management
// AGC: Full featured profile page with admin controls
// AGC: Also supports viewing the current user (owner) as a team member

import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import { useFarms } from '../../context'
import { getTeamMember } from '../../services/teamService'
import { getTasks } from '../../services/tasks'
import { getBackendUrl } from '../../utils/backendUrl'
import type { TeamMember, UserRole as PermissionsUserRole } from '../../types/permissions'
import type { UserRole } from '../../types/user'
import type { Task } from '../../types/task'
import { calculateOccupancy } from '../../utils/conflictDetection'
import { TopBar } from '../shared'

const BASE_URL = getBackendUrl()
import { OccupancyBadge } from '../shared/OccupancyBadge'
import { MemberAgendaList } from './MemberAgendaList'
import { EmployeeCard } from './EmployeeCard'
import { 
  Loader2, Calendar, CheckCircle, Plus, AlertTriangle,
  Settings, Trash2, Clock, Wrench
} from 'lucide-react'
import toast from 'react-hot-toast'

/**
 * AGC: Maps user role from user.ts to permissions.ts UserRole
 * AGC: user.ts UserRole: 'admin' | 'manager' | 'submanager' | 'user'
 * AGC: permissions.ts UserRole: 'admin' | 'manager' | 'worker'
 */
const mapUserRoleToPermissionsRole = (role: UserRole | null | undefined): PermissionsUserRole => {
  const roleMap: Record<string, PermissionsUserRole> = {
    'admin': 'admin',
    'manager': 'manager',
    'submanager': 'manager',
    'user': 'worker'
  }
  return roleMap[role || 'user'] || 'worker'
}

interface MemberProfileProps {
  memberId: string
  onBack: () => void
  onNavigate: (screen: string, params?: any) => void
}

/**
 * AGC: MemberProfile - Complete team member profile page
 * AGC: Features:
 * AGC: - Member info, role, status, permissions
 * AGC: - Occupancy (week/month)
 * AGC: - Task statistics and filtered list
 * AGC: - Hours worked (total from completed tasks)
 * AGC: - Equipment used (from tasks)
 * AGC: - Admin controls (edit permissions, remove member)
 * AGC: - Floating action button to assign tasks
 * AGC: - Supports viewing current user (owner) as a team member
 */
export const MemberProfile: React.FC<MemberProfileProps> = ({
  memberId,
  onBack,
  onNavigate,
}) => {
  const { t } = useTranslation()
  const { getAuthToken, user } = useAuth()
  const { currentFarm } = useFarms()

  const [member, setMember] = useState<TeamMember | null>(null)
  const [tasks, setTasks] = useState<Task[]>([])
  const [allTasks, setAllTasks] = useState<Task[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [occupancyPercentage, setOccupancyPercentage] = useState(0)
  const [weekOccupancy, setWeekOccupancy] = useState(0)
  const [monthOccupancy, setMonthOccupancy] = useState(0)
  const [taskFilter, setTaskFilter] = useState<'pending' | 'completed' | 'all'>('pending')
  const [showPermissionsModal, setShowPermissionsModal] = useState(false)

  // AGC: Check if current user is owner/admin (can manage permissions)
  const isOwner = user?.profile?.role === 'admin' || user?.profile?.role === 'manager'
  
  // AGC: Check if viewing own profile (current user = memberId)
  const isViewingOwnProfile = user?.uid === memberId

  /**
   * AGC: Converts the current auth user to TeamMember format
   * AGC: Used when viewing the owner's own member profile
   */
  const convertUserToTeamMember = useCallback((): TeamMember | null => {
    if (!user?.uid) return null
    
    return {
      id: user.uid,
      uid: user.uid,
      email: user.email || '',
      displayName: user.displayName || user.email || t('team.accountOwner') || 'Account Owner',
      role: mapUserRoleToPermissionsRole(user.profile?.role),
      jobTitle: t('team.accountOwner') || 'Account Owner',
      avatar: user.photoURL || undefined,
      phone: user.phoneNumber || undefined,
      farmIds: [], // AGC: Owner has access to all farms
      permissions: [], // AGC: Owner has all permissions by role
      isActive: true,
      invitedBy: user.uid, // AGC: Self-invited (account owner)
      invitedAt: user.profile?.createdAt || new Date().toISOString(),
      acceptedAt: user.profile?.createdAt || new Date().toISOString(),
      lastActive: new Date().toISOString(),
      createdAt: user.profile?.createdAt || new Date().toISOString(),
      updatedAt: user.profile?.updatedAt || new Date().toISOString(),
    }
  }, [user, t])

  useEffect(() => {
    loadMemberData()
  }, [memberId])

  const loadMemberData = async () => {
    try {
      setIsLoading(true)
      const token = await getAuthToken()
      
      let memberData: TeamMember | null = null
      
      // AGC: Check if viewing own profile (current user)
      if (isViewingOwnProfile) {
        // AGC: Use converted user data instead of fetching from API
        memberData = convertUserToTeamMember()
        console.log('✅ AGC: [MEMBER_PROFILE] Viewing own profile as team member:', memberData?.displayName)
      } else {
        // AGC: Try farm-scoped members API first (new invite system)
        if (currentFarm?.id) {
          try {
            const freshToken = await getAuthToken()
            const res = await fetch(`${BASE_URL}/api/v1/farms/${currentFarm.id}/team`, {
              headers: { Authorization: `Bearer ${freshToken}` },
            })
            if (res.ok) {
              const data = await res.json()
              const farmMember = (data.members || []).find((m: any) => m.uid === memberId || m.id === memberId)
              if (farmMember) {
                memberData = {
                  id: farmMember.id || farmMember.uid,
                  uid: farmMember.uid,
                  email: farmMember.email || '',
                  displayName: farmMember.displayName || farmMember.email || '',
                  role: farmMember.role === 'owner' ? 'admin' : farmMember.role === 'manager' ? 'manager' : 'worker',
                  jobTitle: farmMember.jobTitle || farmMember.role || '',
                  phone: farmMember.phone || undefined,
                  farmIds: [currentFarm.id],
                  permissions: [],
                  isActive: true,
                  invitedBy: farmMember.invitedBy || '',
                  invitedAt: farmMember.joinedAt || new Date().toISOString(),
                  acceptedAt: farmMember.joinedAt || new Date().toISOString(),
                  createdAt: farmMember.joinedAt || new Date().toISOString(),
                  updatedAt: farmMember.joinedAt || new Date().toISOString(),
                  // AGC: Store original farm role for role change UI
                  _farmRole: farmMember.role,
                  _farmId: currentFarm.id,
                } as TeamMember & { _farmRole?: string; _farmId?: string }
              }
            }
          } catch (err) {
            console.warn('AGC: [MEMBER_PROFILE] Farm member lookup failed:', err)
          }
        }

        // AGC: Fallback to old teamMembers collection
        if (!memberData) {
          memberData = await getTeamMember(memberId, token)
        }
      }
      
      // AGC: Fetch all tasks
      const allTasksData = await getTasks(token)

      setMember(memberData)
      setAllTasks(allTasksData)

      // AGC: Filter tasks by assignedToId (use uid for the owner)
      const assigneeId = isViewingOwnProfile ? user?.uid : memberId
      const memberTasks = allTasksData.filter((task) => task.assignedToId === assigneeId)
      setTasks(memberTasks)
      
      console.log(`✅ AGC: [MEMBER_PROFILE] Found ${memberTasks.length} tasks for ${isViewingOwnProfile ? 'owner' : 'member'} (${assigneeId})`)

      // AGC: Calculate occupancy using the correct ID
      const now = new Date()
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      
      const occupancy = calculateOccupancy(
        assigneeId || memberId,
        'user',
        monthStart,
        monthEnd,
        allTasksData
      )
      setOccupancyPercentage(occupancy)
      setMonthOccupancy(occupancy)
      
      // AGC: Week occupancy
      const weekStart = new Date(now)
      weekStart.setDate(now.getDate() - now.getDay())
      const weekEnd = new Date(weekStart)
      weekEnd.setDate(weekStart.getDate() + 7)
      
      const weekOcc = calculateOccupancy(
        assigneeId || memberId,
        'user',
        weekStart,
        weekEnd,
        allTasksData
      )
      setWeekOccupancy(weekOcc)
    } catch (error) {
      console.error('AGC: Error loading member data:', error)
      toast.error(t('team.errors.loadMemberFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  // AGC: Calculate hours worked from completed tasks
  const totalHoursWorked = tasks
    .filter((t) => t.status === 'completed' && t.plannedStart && t.plannedEnd)
    .reduce((total, task) => {
      const start = new Date(task.plannedStart!)
      const end = new Date(task.plannedEnd!)
      const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60)
      return total + hours
    }, 0)

  // AGC: Get unique equipment used
  const equipmentUsed = Array.from(
    new Set(
      tasks
        .filter((t) => t.machineId)
        .map((t) => t.machineId!)
    )
  )

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <TopBar title="Membro" onBack={onBack} />
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </div>
    )
  }

  if (!member) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <TopBar title="Membro" onBack={onBack} />
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Membro não encontrado</p>
        </div>
      </div>
    )
  }

  const pendingTasks = tasks.filter((t) => t.status === 'pending').length
  const inProgressTasks = tasks.filter((t) => t.status === 'in-progress').length
  const completedTasks = tasks.filter((t) => t.status === 'completed').length

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24">
      <TopBar title={member.displayName} onBack={onBack} />

      <div className="px-4 py-4 space-y-4">
        {/* AGC: Member Header Card - Using shared EmployeeCard component */}
        <div>
          <EmployeeCard
            member={member}
            occupationPercentage={occupancyPercentage}
            showRole={true}
            showVerticalIndicator={true}
          />
          
          {/* AGC: Admin Controls (if owner/manager and NOT viewing own profile) */}
          {isOwner && !isViewingOwnProfile && (
            <div className="mt-3 bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 space-y-3">
              {/* AGC: Role change dropdown */}
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">
                  {t('team.changeRole') || 'Alterar Função'}
                </label>
                <select
                  value={(member as any)?._farmRole || member.role || 'worker'}
                  onChange={async (e) => {
                    const newRole = e.target.value
                    const farmId = (member as any)?._farmId || currentFarm?.id
                    if (!farmId) return
                    try {
                      const freshToken = await getAuthToken()
                      const res = await fetch(`${BASE_URL}/api/v1/farms/${farmId}/team/${member.uid || member.id}/role`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${freshToken}` },
                        body: JSON.stringify({ role: newRole }),
                      })
                      if (res.ok) {
                        toast.success(t('team.roleUpdated') || 'Função atualizada')
                        loadMemberData()
                      } else {
                        const data = await res.json().catch(() => ({}))
                        toast.error(data.message || t('team.errors.roleUpdateFailed') || 'Erro ao atualizar função')
                      }
                    } catch {
                      toast.error(t('team.errors.roleUpdateFailed') || 'Erro ao atualizar função')
                    }
                  }}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white"
                >
                  <option value="manager">{t('roleInfo.manager.label') || 'Gerente'}</option>
                  <option value="agronomist">{t('roleInfo.agronomist.label') || 'Agrônomo'}</option>
                  <option value="financial">{t('roleInfo.financial.label') || 'Financeiro'}</option>
                  <option value="worker">{t('roleInfo.worker.label') || 'Operador'}</option>
                  <option value="viewer">{t('roleInfo.viewer.label') || 'Visualizador'}</option>
                </select>
              </div>
              {/* AGC: Deactivate member */}
              <button
                onClick={async () => {
                  if (!confirm(t('team.confirmRemoveMember') || `Remover ${member.displayName}?`)) return
                  const farmId = (member as any)?._farmId || currentFarm?.id
                  if (!farmId) return
                  try {
                    const freshToken = await getAuthToken()
                    const res = await fetch(`${BASE_URL}/api/v1/farms/${farmId}/team/${member.uid || member.id}/deactivate`, {
                      method: 'PATCH',
                      headers: { Authorization: `Bearer ${freshToken}` },
                    })
                    if (res.ok) {
                      toast.success(t('team.memberRemoved', { name: member.displayName }) || 'Membro removido')
                      onBack()
                    } else {
                      const data = await res.json().catch(() => ({}))
                      toast.error(data.message || t('team.errors.removeFailed') || 'Erro ao remover')
                    }
                  } catch {
                    toast.error(t('team.errors.removeFailed') || 'Erro ao remover')
                  }
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                {t('team.removeMember') || 'Remover Membro'}
              </button>
            </div>
          )}
        </div>

        {/* AGC: Overload Warning */}
        {occupancyPercentage > 100 && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
                  {t('team.agendaOverloaded') || 'Agenda Sobrecarregada'}
                </h4>
                <p className="text-xs text-red-700 dark:text-red-300">
                  {t('team.agendaOverloadedDesc') || `Ocupação: ${occupancyPercentage}%. Esta pessoa tem mais tarefas atribuídas do que tempo disponível.`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* AGC: Statistics Grid - Hours, Equipment, Tasks */}
        <div className="grid grid-cols-2 gap-3">
          {/* AGC: Hours Worked */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-blue-500 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {t('team.hoursWorked') || 'Horas Trabalhadas'}
              </span>
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {totalHoursWorked.toFixed(1)}h
            </p>
          </div>

          {/* AGC: Equipment Used */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-orange-500 mb-1">
              <Wrench className="w-4 h-4" />
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {t('team.equipmentUsed') || 'Equipamentos Usados'}
              </span>
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {equipmentUsed.length}
            </p>
          </div>

          {/* AGC: Week Occupancy */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm border border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
              {t('team.weekOccupancy') || 'Esta Semana'}
            </p>
            <div className="flex items-center gap-2">
              <span className={`text-xl font-bold ${
                weekOccupancy > 100 ? 'text-red-500' : 
                weekOccupancy >= 50 ? 'text-yellow-500' : 
                'text-green-500'
              }`}>
                {weekOccupancy}%
              </span>
            </div>
          </div>

          {/* AGC: Month Occupancy */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm border border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
              {t('team.monthOccupancy') || 'Este Mês'}
            </p>
            <div className="flex items-center gap-2">
              <span className={`text-xl font-bold ${
                monthOccupancy > 100 ? 'text-red-500' : 
                monthOccupancy >= 50 ? 'text-yellow-500' : 
                'text-green-500'
              }`}>
                {monthOccupancy}%
              </span>
            </div>
          </div>
        </div>

        {/* AGC: Task Statistics */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-yellow-500 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{t('tasks.statusPending')}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{pendingTasks}</p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-blue-500 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{t('tasks.statusInProgress')}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{inProgressTasks}</p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-green-500 mb-1">
              <CheckCircle className="w-4 h-4" />
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{t('tasks.statusCompleted')}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{completedTasks}</p>
          </div>
        </div>

        {/* AGC: Agenda Section with Filter Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {t('team.agenda') || 'Agenda'}
            </h3>
          </div>
          
          {/* AGC: Filter Tabs */}
          <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
            <button
              onClick={() => setTaskFilter('pending')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                taskFilter === 'pending'
                  ? 'bg-navy-700 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t('team.taskFilter.pending') || 'Pendentes'}
              <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full ${
                taskFilter === 'pending'
                  ? 'bg-white/20 text-white'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
              }`}>
                {pendingTasks + inProgressTasks}
              </span>
            </button>
            <button
              onClick={() => setTaskFilter('completed')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                taskFilter === 'completed'
                  ? 'bg-navy-700 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t('team.taskFilter.completed') || 'Concluídas'}
              <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full ${
                taskFilter === 'completed'
                  ? 'bg-white/20 text-white'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
              }`}>
                {completedTasks}
              </span>
            </button>
            <button
              onClick={() => setTaskFilter('all')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                taskFilter === 'all'
                  ? 'bg-navy-700 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {t('team.taskFilter.all') || 'Todas'}
              <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full ${
                taskFilter === 'all'
                  ? 'bg-white/20 text-white'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
              }`}>
                {tasks.length}
              </span>
            </button>
          </div>

          <MemberAgendaList 
            tasks={tasks} 
            filter={taskFilter}
            onTaskClick={(task) => onNavigate('task-detail', { selectedTaskId: task.id })}
          />
        </div>
      </div>
      
      {/* AGC: Floating Action Button - Assign Task (safe-area-bottom keeps it above BottomNav + Android nav bar) */}
      {/* AGC: Use user.uid when viewing own profile, otherwise use memberId */}
      <button
        onClick={() => onNavigate('new-task', { assignedToId: isViewingOwnProfile ? user?.uid : memberId })}
        className="fixed right-4 bg-navy-700 hover:bg-navy-800 text-white rounded-full p-4 shadow-lg transition-all hover:scale-110 active:scale-95 z-[90] flex items-center gap-2"
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 5rem)' }}
        title={t('team.assignTask') || 'Atribuir Tarefa'}
      >
        <Plus className="w-6 h-6" />
      </button>
    </div>
  )
}

export default MemberProfile
